import _ from 'lodash';

import { useEmployee } from '~/data/employee';
import { useEmployeeEmploymentData } from '~/data/employee/useEmployeeEmploymentData';
import { useSchedulePendingEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useSchedulePendingEmployment';
import { useSelectedEmployeeId } from '~/hooks/employee/useSelectedEmployeeId';

export type ScheduledEmploymentOption = {
  id: number;
  startDate: string;
  endDate: string | undefined;
  addons: string;
  positionIsPending: boolean;
  position: string;
};

export type UseScheduleEmploymentsOutput = {
  scheduledEmploymentOptions: ScheduledEmploymentOption[];
  isLoading: boolean;
  hasUpcomingEmployments: boolean;
  getEmploymentDateRange: (id: string | number) => {
    startDate?: Date;
    endDate?: Date;
  };
};

export const useScheduleEmployments = (): UseScheduleEmploymentsOutput => {
  const { selectedEmployeeId } = useSelectedEmployeeId();
  const { employeeEmploymentData, isLoading: isEmployeeEmploymentDataLoading } =
    useEmployeeEmploymentData();
  const {
    employee,
    isLoading: isEmployeeLoading,
    hasData: hasEmployeeData,
  } = useEmployee();
  const [emplData] = employeeEmploymentData;
  const { data: pendingData, loading: isLoading } =
    useSchedulePendingEmployment(selectedEmployeeId, {
      revalidateOnFocus: false,
    });
  if (isLoading) {
    return {
      scheduledEmploymentOptions: [],
      isLoading,
      hasUpcomingEmployments: false,
      getEmploymentDateRange: (id: string | number) => {
        return {
          startDate: undefined,
          endDate: undefined,
        };
      },
    };
  }
  const upcomingEmployments: ScheduledEmploymentOption[] =
    pendingData
      ?.filter((employments) => !_.isEmpty(employments.employment))
      .map((item) => {
        const { employment, empPosition } = item;
        return {
          id: employment.fEmplId,
          startDate: employment.fEmplDateStart,
          endDate: employment.fEmplTerminationDate,
          addons: '',
          positionIsPending: empPosition.fInternalPositionIsPending,
          position: empPosition.fInternalPositionPosition,
        };
      }) || [];

  const scheduledEmploymentOptions =
    isEmployeeEmploymentDataLoading || isEmployeeLoading || !hasEmployeeData
      ? []
      : [
          {
            id: emplData?.fEmplId,
            startDate: emplData?.fEmplDateStart ?? '',
            endDate: emplData?.fEmplTerminationDate ?? '',
            addons: 'Current',
            positionIsPending: false,
            position: employee.empPosition?.fInternalPositionPosition,
          },
          ...upcomingEmployments,
        ];

  const getEmploymentDateRange = (id: string | number) => {
    const employment = scheduledEmploymentOptions.find(
      (item) => item.id === +id,
    );

    const convertToDateOrUndefined = (date?: string) =>
      !!date ? new Date(date) : undefined;

    return {
      startDate: convertToDateOrUndefined(employment?.startDate),
      endDate: convertToDateOrUndefined(employment?.endDate),
    };
  };

  return {
    scheduledEmploymentOptions,
    isLoading,
    hasUpcomingEmployments: upcomingEmployments.length > 0,
    getEmploymentDateRange,
  };
};
